<template>
  <div>
    <div v-if="isIncidentResolution">
      <v-row
        v-for="manager in localSettings"
        :key="manager.managerRole"
        class="mb-6"
      >
        <v-col cols="12">
          <div class="text-h6 mb-3 role-title" :style="{ color: '#1976D2' }">
            {{ formatManagerRole(manager.managerRole) }}
          </div>

          <v-row>
            <v-col cols="6">
              <v-card class="pl-4">
                <div class="notification-group">
                  <div class="text-subtitle-1 mb-2">Real Time Alerts</div>
                  <div class="ml-4">
                    <v-checkbox
                      v-for="alert in realTimeAlerts"
                      :key="alert.key"
                      v-model="manager[alert.key]"
                      :label="alert.label"
                      density="compact"
                      color="primary"
                      hide-details
                      :readonly="readOnly"
                      :disabled="readOnly"
                    />
                  </div>
                </div>
              </v-card>
            </v-col>

            <v-col cols="6">
              <v-card class="notification-card">
                <div class="notification-group">
                  <div class="text-subtitle-1 mb-2">Summary Reporting</div>
                  <div class="ml-4">
                    <v-checkbox
                      v-for="report in summaryReports"
                      :key="report.key"
                      v-model="manager.summaryReportingSettings[report.key]"
                      :label="report.label"
                      density="compact"
                      color="primary"
                      hide-details
                      :readonly="readOnly"
                      :disabled="readOnly"
                    />
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </div>

    <div v-else>
      <v-alert
        v-if="!hasAnyNotificationEnabled && showValidationError"
        type="error"
        outlined
        class="mb-4"
      >
        {{ validationErrorMessage }}
      </v-alert>

      <div
        v-for="role in managerRoles"
        :key="role.value"
        class="manager-section mb-6"
      >
        <div class="role-header">
          {{ role.label }}
        </div>

        <div class="notification-grid">
          <div class="notification-column">
            <div class="column-header">Assignment Alert</div>
            <div class="checkbox-list">
              <v-checkbox
                v-for="alert in realTimeAlerts"
                :key="`${role.value}-assignment-${alert.key}`"
                v-model="notificationData[role.value].assignment[alert.key]"
                :label="alert.label"
                density="compact"
                color="primary"
                hide-details
                :readonly="readOnly"
                :disabled="readOnly"
                class="notification-checkbox"
              />
            </div>
          </div>

          <div class="notification-column">
            <div class="column-header">To-Do Alerts</div>
            <div class="checkbox-list">
              <v-checkbox
                v-for="alert in realTimeAlerts"
                :key="`${role.value}-todo-${alert.key}`"
                v-model="notificationData[role.value].todo[alert.key]"
                :label="alert.label"
                density="compact"
                color="primary"
                hide-details
                :readonly="readOnly"
                :disabled="readOnly"
                class="notification-checkbox"
              />
            </div>
          </div>

          <div class="notification-column overdue-column">
            <div class="column-header overdue-header">Overdue! Alerts</div>
            <div class="checkbox-list">
              <v-checkbox
                v-for="alert in realTimeAlerts"
                :key="`${role.value}-overdue-${alert.key}`"
                v-model="notificationData[role.value].overdue[alert.key]"
                :label="alert.label"
                density="compact"
                color="primary"
                hide-details
                :readonly="readOnly"
                :disabled="readOnly"
                class="notification-checkbox"
              />
            </div>
          </div>

          <div class="notification-column">
            <div class="column-header">Summary Reporting</div>
            <div class="checkbox-list">
              <v-checkbox
                v-for="report in summaryReports"
                :key="`${role.value}-summary-${report.key}`"
                v-model="notificationData[role.value].summary[report.key]"
                :label="report.label"
                density="compact"
                color="primary"
                hide-details
                :readonly="readOnly"
                :disabled="readOnly"
                class="notification-checkbox"
              />
            </div>
          </div>
        </div>
      </div>

      <v-row v-if="!readOnly">
        <v-col cols="12" class="d-flex justify-end">
          <v-btn
            color="primary"
            large
            :loading="saving"
            :disabled="!hasAnyNotificationEnabled"
            @click="saveSettings"
          >
            SAVE CHANGES
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { titleCase } from "@tod-ui/helpers/strings";

export default {
  name: "NotificationModal",

  props: {
    value: {
      type: [Object, Array],
      default: () => ({}),
    },
    settings: {
      type: [Object, Array],
      default: () => ({}),
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    notificationType: {
      type: String,
      default: "incident_resolution",
    },
  },

  data() {
    return {
      saving: false,
      showValidationError: false,
      localSettings: [],
      notificationData: {},
      isInitializing: false,

      managerRoles: [
        { label: "Facility Manager", value: "facility_manager" },
        { label: "Regional Manager", value: "regional_manager" },
        { label: "Organization Manager", value: "organization_manager" },
      ],

      realTimeAlerts: [
        { key: "email", label: "Email" },
        { key: "robocall", label: "Robo Call" },
        { key: "slack", label: "Slack" },
        { key: "teams", label: "Teams" },
        { key: "sms", label: "SMS" },
      ],

      summaryReports: [
        {
          key: "dailyConcerningActivity",
          label: "Daily Morning Concerning Activity Email",
        },
        {
          key: "weeklyRegionalSummary",
          label: "Weekly Regional Email Summary",
        },
        {
          key: "dailyFacilitySummary",
          label: "Daily Morning Facility Digest Email",
        },
        {
          key: "weeklyFacilitySummary",
          label: "Weekly Facility Email Summary",
        },
      ],
    };
  },

  computed: {
    isIncidentResolution() {
      return this.notificationType === "incident_resolution";
    },

    hasAnyNotificationEnabled() {
      if (this.isIncidentResolution) {
        return this.localSettings.some((manager) => {
          return (
            this.realTimeAlerts.some((alert) => manager[alert.key]) ||
            this.summaryReports.some(
              (report) => manager.summaryReportingSettings?.[report.key]
            )
          );
        });
      } else {
        return this.managerRoles.some((role) => {
          const roleData = this.notificationData[role.value];
          if (!roleData) return false;

          return (
            this.realTimeAlerts.some(
              (alert) =>
                roleData.assignment?.[alert.key] ||
                roleData.todo?.[alert.key] ||
                roleData.overdue?.[alert.key]
            ) ||
            this.summaryReports.some((report) => roleData.summary?.[report.key])
          );
        });
      }
    },

    validationErrorMessage() {
      return "At least one notification preference must be enabled to save changes.";
    },
  },

  watch: {
    value: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined) {
          this.handleDataChange(newVal);
        }
      },
      immediate: true,
    },

    settings: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined) {
          this.handleDataChange(newVal);
        }
      },
      immediate: true,
    },

    // Only apply task rule specific logic for non-incident resolution
    notificationData: {
      handler(newVal) {
        if (this.isInitializing || this.isIncidentResolution) return;

        this.showValidationError = false;
        const backendFormat = this.convertToBackendFormat();
        this.$emit("input", backendFormat);
      },
      deep: true,
    },

    hasAnyNotificationEnabled: {
      handler(hasEnabled) {
        if (this.isInitializing || this.isIncidentResolution) return;

        if (!hasEnabled) {
          this.$emit("validation-error", this.validationErrorMessage);
        }
      },
    },
  },

  created() {
    if (!this.isIncidentResolution) {
      this.initializeDefaultData();
    }
  },

  methods: {
    initializeDefaultData() {
      if (this.isIncidentResolution) return; // Don't interfere with incident resolution

      this.isInitializing = true;
      this.notificationData = this.getDefaultNotificationData();
      this.$nextTick(() => {
        this.isInitializing = false;
      });
    },

    handleDataChange(data) {
      if (this.isIncidentResolution) {
        // Use original logic for incident resolution
        if (!data || Object.keys(data).length === 0) {
          this.localSettings = [];
          return;
        }
        this.localSettings = Array.isArray(data) ? [...data] : [];
        return;
      }

      // Task rule specific logic
      if (!data || Object.keys(data).length === 0) {
        this.initializeDefaultData();
        return;
      }

      this.isInitializing = true;

      this.$nextTick(() => {
        this.initializeData(data);
        this.$nextTick(() => {
          this.isInitializing = false;
        });
      });
    },

    getDefaultNotificationData() {
      return {
        facility_manager: {
          assignment: {
            email: false,
            robocall: false,
            slack: false,
            teams: false,
            sms: false,
          },
          todo: {
            email: false,
            robocall: false,
            slack: false,
            teams: false,
            sms: false,
          },
          overdue: {
            email: false,
            robocall: false,
            slack: false,
            teams: false,
            sms: false,
          },
          summary: {
            dailyConcerningActivity: false,
            weeklyRegionalSummary: false,
            dailyFacilitySummary: false,
            weeklyFacilitySummary: false,
          },
        },
        regional_manager: {
          assignment: {
            email: false,
            robocall: false,
            slack: false,
            teams: false,
            sms: false,
          },
          todo: {
            email: false,
            robocall: false,
            slack: false,
            teams: false,
            sms: false,
          },
          overdue: {
            email: false,
            robocall: false,
            slack: false,
            teams: false,
            sms: false,
          },
          summary: {
            dailyConcerningActivity: false,
            weeklyRegionalSummary: false,
            dailyFacilitySummary: false,
            weeklyFacilitySummary: false,
          },
        },
        organization_manager: {
          assignment: {
            email: false,
            robocall: false,
            slack: false,
            teams: false,
            sms: false,
          },
          todo: {
            email: false,
            robocall: false,
            slack: false,
            teams: false,
            sms: false,
          },
          overdue: {
            email: false,
            robocall: false,
            slack: false,
            teams: false,
            sms: false,
          },
          summary: {
            dailyConcerningActivity: false,
            weeklyRegionalSummary: false,
            dailyFacilitySummary: false,
            weeklyFacilitySummary: false,
          },
        },
      };
    },

    resetNotificationData() {
      if (this.isIncidentResolution) return; // Don't reset incident resolution data

      this.notificationData = this.getDefaultNotificationData();
      this.showValidationError = false;
    },

    initializeData(data) {
      if (this.isIncidentResolution) {
        this.localSettings = Array.isArray(data) ? [...data] : [];
        return;
      }

      if (!data || typeof data !== "object") {
        this.resetNotificationData();
        return;
      }

      this.mapBackendDataToUI(JSON.parse(JSON.stringify(data)));
    },

    mapBackendDataToUI(backendData) {
      if (this.isIncidentResolution) return; // Don't map for incident resolution

      // Reset first to ensure clean state
      this.resetNotificationData();

      // Handle both snake_case and camelCase from backend
      const assignmentData =
        backendData.notify_assignment || backendData.notifyAssignment;
      const todoData = backendData.notify_todo || backendData.notifyTodo;
      const overdueData =
        backendData.notify_overdue || backendData.notifyOverdue;

      if (assignmentData) {
        if (Array.isArray(assignmentData)) {
          assignmentData.forEach((data) =>
            this.mapNotificationTypeData("assignment", data)
          );
        } else {
          this.mapNotificationTypeData("assignment", assignmentData);
        }
      }

      if (todoData) {
        if (Array.isArray(todoData)) {
          todoData.forEach((data) =>
            this.mapNotificationTypeData("todo", data)
          );
        } else {
          this.mapNotificationTypeData("todo", todoData);
        }
      }

      if (overdueData) {
        if (Array.isArray(overdueData)) {
          overdueData.forEach((data) =>
            this.mapNotificationTypeData("overdue", data)
          );
        } else {
          this.mapNotificationTypeData("overdue", overdueData);
        }
      }
    },

    mapNotificationTypeData(notificationType, data) {
      if (this.isIncidentResolution || !data) return;

      // Handle comma-separated manager roles or single role
      const managerRoles =
        data.manager_role || data.managerRole || "organization_manager";
      const roles =
        typeof managerRoles === "string"
          ? managerRoles.split(",")
          : [managerRoles];

      roles.forEach((role) => {
        const trimmedRole = role.trim();

        if (!this.notificationData[trimmedRole]) {
          console.warn(`Unknown manager role: ${trimmedRole}`);
          return;
        }

        // Map notification settings
        this.realTimeAlerts.forEach((alert) => {
          if (data.hasOwnProperty(alert.key)) {
            this.$set(
              this.notificationData[trimmedRole][notificationType],
              alert.key,
              Boolean(data[alert.key])
            );
          }
        });

        // Map summary settings (handle snake_case)
        const summarySettings =
          data.summary_reporting_settings || data.summaryReportingSettings;
        if (summarySettings) {
          this.summaryReports.forEach((report) => {
            const key = report.key;
            const snakeKey = this.camelToSnake(key);

            if (summarySettings.hasOwnProperty(key)) {
              this.$set(
                this.notificationData[trimmedRole].summary,
                key,
                Boolean(summarySettings[key])
              );
            } else if (summarySettings.hasOwnProperty(snakeKey)) {
              this.$set(
                this.notificationData[trimmedRole].summary,
                key,
                Boolean(summarySettings[snakeKey])
              );
            }
          });
        }
      });
    },

    convertToBackendFormat() {
      if (this.isIncidentResolution) {
        // Return original format for incident resolution
        return this.localSettings;
      }

      const result = {
        notifyAssignment: [],
        notifyOverdue: [],
        notifyTodo: [],
      };

      // Create separate entries for each role that has settings
      this.managerRoles.forEach((role) => {
        const roleData = this.notificationData[role.value];
        if (!roleData) return;

        // Check assignment settings
        const hasAssignmentSettings = Object.values(
          roleData.assignment || {}
        ).some((v) => v === true);
        const hasSummarySettings = Object.values(roleData.summary || {}).some(
          (v) => v === true
        );

        if (hasAssignmentSettings || hasSummarySettings) {
          result.notifyAssignment.push({
            ...roleData.assignment,
            managerRole: role.value,
            summaryReportingSettings: { ...roleData.summary },
          });
        }

        // Check todo settings
        const hasTodoSettings = Object.values(roleData.todo || {}).some(
          (v) => v === true
        );
        if (hasTodoSettings || hasSummarySettings) {
          result.notifyTodo.push({
            ...roleData.todo,
            managerRole: role.value,
            summaryReportingSettings: { ...roleData.summary },
          });
        }

        // Check overdue settings
        const hasOverdueSettings = Object.values(roleData.overdue || {}).some(
          (v) => v === true
        );
        if (hasOverdueSettings || hasSummarySettings) {
          result.notifyOverdue.push({
            ...roleData.overdue,
            managerRole: role.value,
            summaryReportingSettings: { ...roleData.summary },
          });
        }
      });

      return result;
    },

    async saveSettings() {
      this.saving = true;

      try {
        if (!this.isIncidentResolution && !this.hasAnyNotificationEnabled) {
          this.showValidationError = true;
          this.$emit("validation-error", this.validationErrorMessage);
          return;
        }

        const backendFormat = this.convertToBackendFormat();
        this.$emit("save", backendFormat);
        this.$emit("input", backendFormat);
      } catch (error) {
        console.error("Error saving notification settings:", error);
        this.$emit("validation-error", "Error saving notification settings");
      } finally {
        this.saving = false;
      }
    },

    formatManagerRole(role) {
      if (!role) return "";
      return titleCase(role.replace(/_/g, " "));
    },

    camelToSnake(str) {
      return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    },

    titleCase,
  },
};
</script>

<style scoped>
.notification-group {
  padding: 16px;
}
.role-header {
  font-size: 18px;
  font-weight: 600;
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.notification-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}

.notification-column {
  border-right: 1px solid #e5e7eb;
  min-height: 300px;
}

.notification-column:last-child {
  border-right: none;
}

.overdue-column {
  background-color: #fef3f2;
}

.column-header {
  padding: 12px 16px;
  font-weight: 600;
  font-size: 14px;
  background-color: #f1f5f9;
  text-align: center;
}

.overdue-header {
  background-color: #fee2e2;
  color: #dc2626;
}

.checkbox-list {
  padding: 16px;
}

.notification-checkbox >>> .v-input__control {
  min-height: auto !important;
}

.notification-checkbox >>> .v-messages {
  display: none !important;
}

.notification-checkbox >>> .v-input--selection-controls__input {
  margin-right: 8px !important;
}

.notification-checkbox >>> .v-label {
  font-size: 15px !important;
  line-height: 1.6 !important;
}

/* Responsive */
@media (max-width: 1200px) {
  .notification-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .notification-column {
    min-height: 200px;
  }
}

@media (max-width: 768px) {
  .notification-grid {
    grid-template-columns: 1fr;
  }

  .notification-column {
    min-height: 150px;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .notification-column:last-child {
    border-bottom: none;
  }
}
</style>
